import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ACCURACY_COARSE_M,
  CHECKIN_HISTORY_DAYS,
  LIVE_DURATIONS_MIN,
  LIVE_UPDATE_MS,
  PLACE_NAME_MAX,
  PLACE_RADIUS_DEFAULT_M,
  checkinTime,
  isGeoPoint,
  isLiveActive,
  latestPerPerson,
  mapsUrl,
  nearestPlace,
  placeAt,
} from '@kraftwerk/shared';
import type { CheckinKind } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import type { LocalCheckin, LocalHouseholdMember, LocalPlace } from '../../db/local.js';
import {
  addPlace,
  checkIn,
  deleteCheckin,
  deletePlace,
  heldLocationCount,
  listCheckins,
  listPlaces,
  setLocationSharing,
} from '../../db/location.js';
import type { Fix } from '../../db/location.js';
import { listMembers } from '../../db/shopping.js';
import { formatDay } from '../../format.js';
import { useIsChild } from '../../hooks/useModules.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { geolocate, GeoError } from '../../location/geolocate.js';
import type { GeoFailure } from '../../location/geolocate.js';
import {
  liveState,
  startLiveShare,
  stopLiveShare,
  subscribeLive,
} from '../../location/liveShare.js';
import { syncOnce } from '../../sync/engine.js';

const RADII = [100, PLACE_RADIUS_DEFAULT_M, 300, 500] as const;

function failureOf(error: unknown): GeoFailure {
  return error instanceof GeoError ? error.reason : 'unavailable';
}

/** Meter lesbar: „80 m“, „1,2 km“. */
function useDistance(): (meters: number) => string {
  const { i18n } = useTranslation();
  return (meters) =>
    meters < 1000
      ? `${Math.round(meters)} m`
      : `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(meters / 1000)} km`;
}

/**
 * Standort im Haushalt (27.10.2026) — unter Haushalt → Mitglieder, kein
 * eigener Reiter. Teilen geht nur im Vordergrund (eine Web-App bekommt im
 * Hintergrund keinen Ort); die Ansicht sagt das, statt so zu tun, als liefe
 * etwas weiter. Eine Karte kommt später — jetzt eine Liste.
 */
export function LocationPanel({
  householdId,
  focus = false,
}: {
  householdId: string | null;
  /** Aus einer Mitteilung geöffnet: Überschrift in den Blick. */
  focus?: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const child = useIsChild();
  const me = currentOwnerId();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(tick);
  }, []);
  useEffect(() => {
    if (!focus) return;
    headingRef.current?.focus();
    headingRef.current?.scrollIntoView?.({ block: 'start' });
  }, [focus]);

  const members = useLiveQuery(
    () => listMembers(householdId, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const places = useLiveQuery(() => listPlaces(householdId, db), [householdId], [] as LocalPlace[]);
  // Minutengenau reicht: Die Aufbewahrung zählt Tage.
  const minute = Math.floor(now.getTime() / 60_000);
  const checkins = useLiveQuery(
    () => listCheckins(householdId, new Date(minute * 60_000), db),
    [householdId, minute],
    [] as LocalCheckin[],
  );
  const held = useLiveQuery(() => heldLocationCount(householdId, db), [householdId], 0);

  if (!householdId) return null;
  const mine = members.find((member) => member.userId === me);
  // Kinder teilen nur, wenn die Eltern es eingeschaltet haben — der Server prüft es auch.
  const mayShare = !child || mine?.locationSharing === true;

  return (
    <section aria-labelledby="location-heading" className="kw-tile flex flex-col gap-3">
      <h2 id="location-heading" ref={headingRef} tabIndex={-1} className="kw-kicker">
        {t('location.heading')}
      </h2>
      <p className="text-sm text-ink-muted">{t('location.intro')}</p>
      {held > 0 ? (
        <p role="status" className="text-sm text-warn">
          {t('location.held', { count: held })}
        </p>
      ) : null}
      {mayShare ? (
        <CheckinActions householdId={householdId} places={places} held={held > 0} />
      ) : (
        <p role="status" className="text-sm text-ink-muted">
          {t('location.childOff')}
        </p>
      )}
      <WhoIsWhere
        checkins={checkins}
        members={members}
        places={places}
        now={now}
        me={me}
        child={child}
      />
      <PlaceList householdId={householdId} places={places} mayEdit={!child} />
    </section>
  );
}

type Pending = { kind: CheckinKind; fix: Fix; placeId: string | null } | null;

function CheckinActions({
  householdId,
  places,
  held,
}: {
  householdId: string;
  places: LocalPlace[];
  /** Ohne Haushaltsschlüssel wartet es — dann nicht „geteilt“ behaupten. */
  held: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const distance = useDistance();
  const [busy, setBusy] = useState<CheckinKind | null>(null);
  const [error, setError] = useState<GeoFailure | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [minutes, setMinutes] = useState<number>(LIVE_DURATIONS_MIN[1]);
  const [sent, setSent] = useState(false);
  const live = useSyncExternalStore(subscribeLive, liveState, liveState);
  const timeZone = useTimeZone();

  const locate = (kind: CheckinKind) => {
    setBusy(kind);
    setError(null);
    setSent(false);
    geolocate()
      .then((fix) => setPending({ kind, fix, placeId: placeAt(fix, places)?.id ?? null }))
      .catch((failure: unknown) => setError(failureOf(failure)))
      .finally(() => setBusy(null));
  };

  const send = async () => {
    if (!pending) return;
    const place = places.find((entry) => entry.id === pending.placeId) ?? null;
    const chosen = place ? { id: place.id, name: place.name } : null;
    if (pending.kind === 'live') {
      await startLiveShare({ householdId, minutes, fix: pending.fix, place: chosen });
    } else {
      await checkIn({ householdId, kind: pending.kind, fix: pending.fix, place: chosen });
    }
    setPending(null);
    setSent(true);
  };

  const clock = (iso: string) =>
    new Intl.DateTimeFormat('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(new Date(iso));

  if (pending) {
    const near = nearestPlace(pending.fix, places);
    return (
      <form
        aria-labelledby="location-confirm"
        className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <p id="location-confirm" className="kw-label">
          {t(`location.confirm.${pending.kind}`)}
        </p>
        <Accuracy meters={pending.fix.accuracyM} />
        {places.length > 0 ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="kw-label">{t('location.placeChoice')}</legend>
            <div className="flex flex-wrap gap-2">
              {places.map((place) => (
                <button
                  key={place.id}
                  type="button"
                  aria-pressed={pending.placeId === place.id}
                  onClick={() => setPending({ ...pending, placeId: place.id })}
                  className="kw-toggle"
                >
                  {place.name}
                </button>
              ))}
              <button
                type="button"
                aria-pressed={pending.placeId === null}
                onClick={() => setPending({ ...pending, placeId: null })}
                className="kw-toggle"
              >
                {t('location.noPlace')}
              </button>
            </div>
            {near && !near.inside ? (
              <p className="text-xs text-ink-muted">
                {t('location.outside', {
                  place: near.place.name,
                  distance: distance(near.distanceM),
                })}
              </p>
            ) : null}
          </fieldset>
        ) : null}
        {pending.kind === 'live' ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="kw-label">{t('location.duration')}</legend>
            <div className="flex flex-wrap gap-2">
              {LIVE_DURATIONS_MIN.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={minutes === option}
                  onClick={() => setMinutes(option)}
                  className="kw-toggle"
                >
                  {t('location.minutes', { count: option })}
                </button>
              ))}
            </div>
            <p className="text-xs text-warn">
              {t('location.liveForeground', { minutes: LIVE_UPDATE_MS / 60_000 })}
            </p>
          </fieldset>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button type="submit" className="kw-btn">
            {t('location.send')}
          </button>
          <button type="button" className="kw-btn kw-btn-quiet" onClick={() => setPending(null)}>
            {t('location.cancel')}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          className="kw-btn"
          disabled={busy !== null}
          onClick={() => locate('arrived')}
        >
          {busy === 'arrived' ? t('location.locating') : t('location.arrived')}
        </button>
        <button
          type="button"
          className="kw-btn kw-btn-quiet"
          disabled={busy !== null}
          onClick={() => locate('here')}
        >
          {busy === 'here' ? t('location.locating') : t('location.here')}
        </button>
      </div>
      {live ? (
        <div role="status" className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3">
          <p className="text-sm text-good">{t('location.liveOn', { time: clock(live.until) })}</p>
          <p className="text-xs text-warn">
            {t('location.liveForeground', { minutes: LIVE_UPDATE_MS / 60_000 })}
          </p>
          {live.lastError ? (
            <p className="text-xs text-warn">{t(`location.error.${live.lastError}`)}</p>
          ) : null}
          <button
            type="button"
            className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
            onClick={() => void stopLiveShare()}
          >
            {t('location.liveStop')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="kw-btn kw-btn-quiet"
          disabled={busy !== null}
          onClick={() => locate('live')}
        >
          {busy === 'live' ? t('location.locating') : t('location.live')}
        </button>
      )}
      {sent ? (
        <p role="status" className="text-sm text-good">
          {held ? t('location.savedHeld') : t('location.sent')}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {t(`location.error.${error}`)}
        </p>
      ) : null}
    </div>
  );
}

function Accuracy({ meters }: { meters: number | null }): React.JSX.Element {
  const { t } = useTranslation();
  const distance = useDistance();
  if (meters === null) {
    return <p className="text-xs text-ink-muted">{t('location.accuracyUnknown')}</p>;
  }
  return (
    <p className={`text-xs ${meters > ACCURACY_COARSE_M ? 'text-warn' : 'text-ink-muted'}`}>
      {meters > ACCURACY_COARSE_M
        ? t('location.accuracyCoarse', { distance: distance(meters) })
        : t('location.accuracy', { distance: distance(meters) })}
    </p>
  );
}

function WhoIsWhere({
  checkins,
  members,
  places,
  now,
  me,
  child,
}: {
  checkins: LocalCheckin[];
  members: LocalHouseholdMember[];
  places: LocalPlace[];
  now: Date;
  me: string | null;
  child: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const latest = latestPerPerson(checkins);
  const latestIds = new Set(latest.map((checkin) => checkin.id));
  const history = checkins.filter((checkin) => !latestIds.has(checkin.id));
  const nameOf = (userId: string | undefined) =>
    userId === me
      ? t('location.me')
      : (members.find((member) => member.userId === userId)?.displayName ?? t('location.someone'));

  return (
    <div className="flex flex-col gap-2">
      <h3 className="kw-label">{child ? t('location.mine') : t('location.whoIsWhere')}</h3>
      {latest.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('location.none')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {latest.map((checkin) => (
            <CheckinRow
              key={checkin.id}
              checkin={checkin}
              name={nameOf(checkin.userId)}
              places={places}
              now={now}
              own={checkin.userId === me}
            />
          ))}
        </ul>
      )}
      {history.length > 0 ? (
        <details>
          <summary className="cursor-pointer py-2 text-sm">
            {t('location.history', { count: history.length })}
          </summary>
          <ul className="flex flex-col gap-2">
            {history.map((checkin) => (
              <CheckinRow
                key={checkin.id}
                checkin={checkin}
                name={nameOf(checkin.userId)}
                places={places}
                now={now}
                own={checkin.userId === me}
              />
            ))}
          </ul>
        </details>
      ) : null}
      <p className="text-xs text-ink-muted">
        {t('location.retention', { days: CHECKIN_HISTORY_DAYS })}
      </p>
    </div>
  );
}

function CheckinRow({
  checkin,
  name,
  places,
  now,
  own,
}: {
  checkin: LocalCheckin;
  name: string;
  places: LocalPlace[];
  now: Date;
  own: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const distance = useDistance();
  const timeZone = useTimeZone();
  const point = { latitude: checkin.latitude, longitude: checkin.longitude };
  const at = checkinTime(checkin);
  const when = new Date(at);
  const day = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(when);
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(when);
  const near = isGeoPoint(point) ? nearestPlace(point, places) : null;
  const where = checkin.placeName
    ? checkin.placeName
    : near
      ? t('location.unnamedNear', { distance: distance(near.distanceM), place: near.place.name })
      : t('location.unnamed');
  // Mit der Uhr von jetzt, nicht dem 30-s-Takt: Endet das Teilen (App im
  // Hintergrund), liegt `liveUntil` sonst noch hinter dem letzten Takt, und das
  // eigene Gerät zeigte weiter „teilt gerade“ (Geräteabnahme 28.10.2026).
  const live = isLiveActive(checkin, new Date(Math.max(now.getTime(), Date.now())));

  return (
    <li className="flex flex-col gap-1 border-t border-line pt-2 text-sm">
      <p>
        <span className="font-medium">{name}</span>
        {' · '}
        {t(`location.kind.${checkin.kind ?? 'here'}`)}
        {': '}
        {where}
      </p>
      <p className="text-xs text-ink-muted">
        {t('location.when', { day: formatDay(day, 'short', i18n.language), time })}
        {live ? ` · ${t('location.liveBadge')}` : ''}
      </p>
      <Accuracy meters={checkin.accuracyM} />
      <div className="flex flex-wrap gap-2">
        {isGeoPoint(point) ? (
          <a
            href={mapsUrl(point, checkin.placeName ?? name)}
            target="_blank"
            rel="noreferrer noopener"
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('location.openMaps')}
          </a>
        ) : null}
        {own ? (
          <button
            type="button"
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
            onClick={() => void deleteCheckin(checkin.id)}
          >
            {t('location.withdraw')}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function PlaceList({
  householdId,
  places,
  mayEdit,
}: {
  householdId: string;
  places: LocalPlace[];
  mayEdit: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const distance = useDistance();
  const ids = { name: useId() };
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [radius, setRadius] = useState<number>(PLACE_RADIUS_DEFAULT_M);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<GeoFailure | null>(null);

  const save = () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    geolocate()
      .then((fix) => addPlace({ householdId, name, point: fix, radiusM: radius }))
      .then(() => {
        setAdding(false);
        setName('');
      })
      .catch((failure: unknown) => setError(failureOf(failure)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="kw-label">{t('location.places')}</h3>
      {places.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('location.noPlaces')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {places.map((place) => (
            <li key={place.id} className="flex min-h-11 items-center justify-between gap-2 text-sm">
              <span>
                {place.name}
                <span className="ml-2 text-xs text-ink-muted">
                  {t('location.radius', { distance: distance(place.radiusM ?? 0) })}
                </span>
              </span>
              {mayEdit ? (
                <button
                  type="button"
                  className="kw-btn kw-btn-quiet kw-btn-small px-3"
                  aria-label={t('location.deletePlace', { place: place.name })}
                  onClick={() => void deletePlace(place.id)}
                >
                  {t('location.delete')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {mayEdit && !adding ? (
        <button
          type="button"
          className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
          onClick={() => setAdding(true)}
        >
          {t('location.addPlace')}
        </button>
      ) : null}
      {mayEdit && adding ? (
        <form
          className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <label htmlFor={ids.name} className="flex flex-col gap-1">
            <span className="kw-label">{t('location.placeName')}</span>
            <input
              id={ids.name}
              className="kw-field"
              value={name}
              maxLength={PLACE_NAME_MAX}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </label>
          <fieldset className="flex flex-col gap-2">
            <legend className="kw-label">{t('location.placeRadius')}</legend>
            <div className="flex flex-wrap gap-2">
              {RADII.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={radius === option}
                  onClick={() => setRadius(option)}
                  className="kw-toggle"
                >
                  {distance(option)}
                </button>
              ))}
            </div>
          </fieldset>
          <p className="text-xs text-ink-muted">{t('location.placeHint')}</p>
          <div className="flex flex-wrap gap-2">
            <button type="submit" className="kw-btn" disabled={busy || !name.trim()}>
              {busy ? t('location.locating') : t('location.savePlace')}
            </button>
            <button type="button" className="kw-btn kw-btn-quiet" onClick={() => setAdding(false)}>
              {t('location.cancel')}
            </button>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {t(`location.error.${error}`)}
            </p>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}

/** Für die Eltern: Standort eines Kindes an/aus — ab Werk aus, schreibt nur der Server. */
export function ChildLocationSetting({
  child,
}: {
  child: LocalHouseholdMember;
}): React.JSX.Element {
  const { t } = useTranslation();
  const on = child.locationSharing === true;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const toggle = () => {
    setBusy(true);
    setError(false);
    setLocationSharing(child.userId, !on)
      // Die Mitgliedschaft schreibt der Server — erst der Abgleich zeigt den neuen Stand.
      .then(() => syncOnce())
      .catch(() => setError(true))
      .finally(() => setBusy(false));
  };
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-pressed={on}
        disabled={busy}
        onClick={toggle}
        className="kw-toggle self-start"
      >
        {t('location.childToggle')}
      </button>
      <p className="text-xs text-ink-muted">
        {on
          ? t('location.childOnHint', { name: child.displayName })
          : t('location.childOffHint', { name: child.displayName })}
      </p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {t('children.offline')}
        </p>
      ) : null}
    </div>
  );
}
